import React, { useState, useEffect, useMemo } from "react";

const MEDALS = ["🥇", "🥈", "🥉"];

export default function EmojiLeaderboard({ game }) {
  const [loading, setLoading] = useState(true);
  const [selectedGrid, setSelectedGrid] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSpeed, setSelectedSpeed] = useState(null);

  const leaderboard = game.leaderboard || [];

  // Load leaderboard on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (game.loadLeaderboard) {
          await game.loadLeaderboard();
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [game.loadLeaderboard]);

  // Available Filters
  const gridSizes = useMemo(() => {
    return [...new Set(leaderboard.map((x) => Number(x.gridSize)).filter(Boolean))].sort((a, b) => a - b);
  }, [leaderboard]);

  const categories = useMemo(() => {
    return [...new Set(
      leaderboard
        .filter((x) => Number(x.gridSize) === Number(selectedGrid))
        .map((x) => x.category)
        .filter(Boolean)
    )];
  }, [leaderboard, selectedGrid]);

  const speeds = useMemo(() => {
    return [...new Set(
      leaderboard
        .filter((x) => 
          Number(x.gridSize) === Number(selectedGrid) && 
          x.category === selectedCategory
        )
        .map((x) => x.speed)
        .filter(Boolean)
    )];
  }, [leaderboard, selectedGrid, selectedCategory]);

  // Auto-select defaults
  useEffect(() => {
    if (gridSizes.length && selectedGrid === null) setSelectedGrid(gridSizes[0]);
  }, [gridSizes, selectedGrid]);

  useEffect(() => {
    if (categories.length && selectedCategory === null) setSelectedCategory(categories[0]);
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (speeds.length && selectedSpeed === null) setSelectedSpeed(speeds[0]);
  }, [speeds, selectedSpeed]);

  // Filtered & Sorted Leaderboard
  const filteredLeaderboard = useMemo(() => {
    let list = [...leaderboard];

    if (selectedGrid) list = list.filter(x => Number(x.gridSize) === Number(selectedGrid));
    if (selectedCategory) list = list.filter(x => x.category === selectedCategory);
    if (selectedSpeed) list = list.filter(x => x.speed === selectedSpeed);

    list.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));

    return list;
  }, [leaderboard, selectedGrid, selectedCategory, selectedSpeed]);

  const formatTime = (ms) => (Number(ms || 0) / 1000).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white flex flex-col">
      {/* HEADER */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-3 py-3 border-b border-cyan-500/20 bg-slate-950/90 backdrop-blur">
        <button
          onClick={() => game.setScreen("home")}
          className="w-fit px-2 py-1 text-xs lg:text-sm text-cyan-300 hover:text-cyan-100"
        >
          ← Back
        </button>

        <div className="text-lg lg:text-xl font-black text-cyan-300">🏆 Leaderboard</div>
        <div className="w-8" />
      </div>

      {/* GRID FILTER */}
      {gridSizes.length > 0 && (
        <div className="px-2 py-1 border-b border-slate-800">
          <div className="text-xs text-slate-400 mb-2">Grid Size</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {gridSizes.map((size) => (
              <button
                key={size}
                onClick={() => {
                  setSelectedGrid(size);
                  setSelectedCategory(null);
                  setSelectedSpeed(null);
                }}
                className={`w-fit px-2 py-1 rounded-xl font-bold whitespace-nowrap transition ${
                  selectedGrid === size ? "bg-cyan-500 text-black" : "bg-slate-800 hover:bg-slate-700"
                }`}
              >
                🟦 {size}×{size}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CATEGORY FILTER */}
      {categories.length > 0 && (
        <div className="px-2 py-1 border-b border-slate-800">
          <div className="text-xs text-slate-400 mb-2">Category</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCategory(cat);
                  setSelectedSpeed(null);
                }}
                className={`w-fit px-2 py-1 rounded-xl capitalize whitespace-nowrap transition ${
                  selectedCategory === cat ? "bg-pink-500 text-white" : "bg-slate-800 hover:bg-slate-700"
                }`}
              >
                🌈 {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SPEED FILTER */}
      {speeds.length > 0 && (
        <div className="px-1 py-1 border-b border-slate-800">
          <div className="text-xs text-slate-400 mb-2">Speed</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {speeds.map((sp) => (
              <button
                key={sp}
                onClick={() => setSelectedSpeed(sp)}
                className={`w-fit px-2 py-1 rounded-xl capitalize whitespace-nowrap transition ${
                  selectedSpeed === sp ? "bg-emerald-500 text-black" : "bg-slate-800 hover:bg-slate-700"
                }`}
              >
                ⚡ {sp}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LEADERBOARD LIST */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading ? (
          <div className="py-20 text-center">
            <div className="text-5xl animate-pulse">🏆</div>
            <div className="mt-4 text-slate-400">Loading Leaderboard...</div>
          </div>
        ) : filteredLeaderboard.length === 0 ? (
          <div className="py-20 text-center text-slate-400">
            No entries found for this filter.
          </div>
        ) : (
          filteredLeaderboard.map((entry, index) => {
            const score = Number(entry.score) || 0;
            const xp = Number(entry.xp) || 0;
            const accuracy = Number(entry.accuracy) || 0;

            return (
              <div
                key={entry.userId || index}
                className={`rounded-2xl p-4 transition-all ${
                  index < 3 ? "border border-yellow-400/30 bg-yellow-500/5" : "bg-slate-900 border border-slate-700"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 text-center text-3xl">
                    {index < 3 ? MEDALS[index] : `#${index + 1}`}
                  </div>

                  <div className="flex-1">
                    <div className="font-bold">{entry.name || "Anonymous Hunter"}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      🟦 {entry.gridSize}×{entry.gridSize} • 🌈 {entry.category} • ⚡ {entry.speed}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl font-black text-yellow-400">🏆 {score}</div>
                    <div className="text-xs text-emerald-400">⭐ {xp} XP</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="bg-slate-800 rounded-xl py-2">
                    🔥 <span className="font-bold text-pink-400">{entry.bestCombo || 0}</span>
                  </div>
                  <div className="bg-slate-800 rounded-xl py-2">
                    🎯 <span className="font-bold text-cyan-300">{accuracy}%</span>
                  </div>
                  <div className="bg-slate-800 rounded-xl py-2">
                    ⏱ <span className="font-bold text-orange-300">{formatTime(entry.timeTaken)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}